import type { VespAsset, ApiResponse, SearchParams, LibraryStats } from '../types';

const BASE = '/api';

async function request<T>(path: string, options?: RequestInit & { timeoutMs?: number }): Promise<T> {
  const { timeoutMs, ...init } = options ?? {};
  const controller = new AbortController();
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      ...init,
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export const api = {
  // Assets
  listAssets(params: SearchParams = {}): Promise<ApiResponse<VespAsset[]>> {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    if (params.category) qs.set('category', params.category);
    if (params.media_type) qs.set('media_type', params.media_type);
    if (params.model_name) qs.set('model_name', params.model_name);
    if (params.project_id) qs.set('project_id', params.project_id);
    if (params.is_favorite !== undefined) qs.set('is_favorite', String(params.is_favorite));
    return request(`/assets?${qs}`);
  },

  getAsset(id: string): Promise<ApiResponse<VespAsset>> {
    return request(`/assets/${id}`);
  },

  createAsset(data: Partial<VespAsset>): Promise<ApiResponse<VespAsset>> {
    return request('/assets', { method: 'POST', body: JSON.stringify(data) });
  },

  updateAsset(id: string, data: Partial<VespAsset>): Promise<ApiResponse<VespAsset>> {
    return request(`/assets/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  },

  deleteAsset(id: string): Promise<ApiResponse<unknown>> {
    return request(`/assets/${id}`, { method: 'DELETE' });
  },

  toggleFavorite(id: string): Promise<ApiResponse<VespAsset>> {
    return request(`/assets/${id}/favorite`, { method: 'POST' });
  },

  // Search
  search(params: SearchParams): Promise<ApiResponse<VespAsset[]>> {
    return request('/search', { method: 'POST', body: JSON.stringify(params) });
  },

  // Upload（multipart：文件 + prompt/title/tags）
  async uploadAsset(file: File, fields: { prompt?: string; title?: string; tags?: string; category?: string }): Promise<ApiResponse<VespAsset>> {
    const form = new FormData();
    form.append('file', file);
    if (fields.prompt) form.append('prompt', fields.prompt);
    if (fields.title) form.append('title', fields.title);
    if (fields.tags) form.append('tags', fields.tags);
    if (fields.category) form.append('category', fields.category);
    const res = await fetch(`${BASE}/upload`, { method: 'POST', body: form });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.error || `Upload failed: ${res.status}`);
    }
    return res.json();
  },

  // Stats
  getStats(): Promise<ApiResponse<LibraryStats>> {
    return request('/stats');
  },
};
